/**
 * SystemicSafetyCard — какво означават лекарствата и заболяванията за устата.
 * Данни: GET /api/dental-intelligence/systemic-safety
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ShieldAlert, AlertTriangle, Info, Droplet, HelpCircle } from "lucide-react";
import { useAuth } from "@/App";

const SEV = {
  warn: { color: "#F59E0B", Icon: AlertTriangle },
  info: { color: "#0EA5E9", Icon: Info },
};

export default function SystemicSafetyCard({ isDark = false, cardCls = "" }) {
  const { API } = useAuth();
  const [d, setD] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/dental-intelligence/systemic-safety`);
      setD(data);
    } catch { setD(null); }
  }, [API]);

  useEffect(() => { load(); }, [load]);
  if (!d) return null;

  const border = isDark ? "border-white/10" : "border-gray-200";
  const rows = [
    ...(d.medication_findings || []).map((f) => ({ ...f, from: f.group_bg })),
    ...(d.condition_findings || []).map((f) => ({ ...f, from: null })),
  ];

  return (
    <div className={cardCls || `p-4 rounded-2xl border ${border}`} data-testid="dhi-systemic-safety-card">
      <div className="text-sm font-bold inline-flex items-center gap-1.5">
        <ShieldAlert className="w-4 h-4 text-cyan-500" /> Лекарства, заболявания и устата
      </div>
      <p className="text-[11px] opacity-70 mt-0.5" data-testid="dhi-systemic-summary">
        {d.summary_bg}
      </p>

      <div className="mt-3 space-y-1.5">
        {rows.map((f, i) => {
          const s = SEV[f.severity] || SEV.info;
          return (
            <div key={i} className={`p-2.5 rounded-xl border ${border}`}
              data-testid={`dhi-systemic-row-${i}`}>
              <div className="flex items-start gap-1.5 text-[11px]">
                <s.Icon className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: s.color }} />
                <div>
                  <span className="font-bold">{f.title_bg}</span>
                  {f.from && <span className="opacity-60"> · {f.from}</span>}
                  {!!f.medications?.length && (
                    <span className="opacity-60"> ({f.medications.join(", ")})</span>
                  )}
                  <div className="opacity-80 mt-0.5">{f.advice_bg}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {d.dry_mouth?.detected && (
        <div className={`mt-3 p-2.5 rounded-xl border ${border}`} data-testid="dhi-systemic-dry-mouth">
          <div className="text-[11px] font-bold inline-flex items-center gap-1.5">
            <Droplet className="w-3.5 h-3.5 text-sky-500" /> План за суха уста
          </div>
          <ul className="mt-1 space-y-0.5">
            {d.dry_mouth.steps_bg.map((s, i) => (
              <li key={i} className="text-[11px] opacity-85">· {s}</li>
            ))}
          </ul>
        </div>
      )}

      {d.endocarditis_prophylaxis?.indicated && (
        <div className="mt-3 p-2.5 rounded-xl bg-rose-500/10 border border-rose-500/30"
          data-testid="dhi-systemic-endocarditis">
          <div className="text-[11px] font-bold text-rose-500">
            Антибиотична профилактика преди зъболекар
          </div>
          <div className="text-[11px] opacity-85 mt-0.5">
            {d.endocarditis_prophylaxis.note_bg}
          </div>
        </div>
      )}

      {!!d.questions_bg?.length && (
        <div className={`mt-3 p-2.5 rounded-xl border ${border}`} data-testid="dhi-systemic-questions">
          <div className="text-[10px] font-bold uppercase opacity-60 inline-flex items-center gap-1">
            <HelpCircle className="w-3.5 h-3.5" /> Въпроси към зъболекаря
          </div>
          <ul className="mt-1 space-y-0.5">
            {d.questions_bg.map((q, i) => (
              <li key={i} className="text-[11px] opacity-85" data-testid={`dhi-systemic-question-${i}`}>
                · {q}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="text-[9px] opacity-50 mt-2">{d.note_bg}</div>
    </div>
  );
}
